import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { SUPER_ADMIN_EMAIL } from '@/hooks/useAdminRole';

interface UserAccount {
  user_id: string;
  email: string;
  full_name: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed: boolean;
  booking_count: number;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return 'Never signed in';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function AccountManager() {
  const [users, setUsers] = useState<UserAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const loadUsers = useCallback(async (showLoading: boolean) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.rpc('list_all_users_with_bookings');
      if (err) throw err;
      setUsers((data || []) as UserAccount[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers(true);
  }, [loadUsers]);

  const stats = useMemo(() => {
    const total = users.length;
    const verified = users.filter((u) => u.email_confirmed).length;
    return { total, verified };
  }, [users]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.full_name || '').toLowerCase().includes(q),
    );
  }, [users, query]);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
        {[
          { label: 'Total accounts', value: stats.total, icon: 'ri-user-line', cls: 'text-secondary-900 bg-secondary-100' },
          { label: 'Email verified', value: stats.verified, icon: 'ri-mail-check-line', cls: 'text-green-700 bg-green-100' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-lg border border-secondary-200 p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${s.cls}`}>
              <i className={`${s.icon} text-lg`} />
            </div>
            <div>
              <p className="font-heading text-2xl font-bold text-secondary-900">{s.value}</p>
              <p className="text-xs text-secondary-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="bg-white rounded-lg border border-secondary-200 p-4 sm:p-5 flex-1">
          <h3 className="font-heading text-lg font-bold text-secondary-900">All registered accounts</h3>
          <p className="mt-1 text-sm text-secondary-500">
            Every traveler who signed up, with their full account details.
          </p>
        </div>
        <div className="relative sm:max-w-xs w-full">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400 text-sm" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or email..."
            className="w-full text-sm rounded-full border border-secondary-300 bg-white pl-9 pr-4 py-2.5 text-secondary-900 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3 text-secondary-500">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
            <span className="text-sm">Loading accounts...</span>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl text-red-500" />
          </div>
          <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">Something went wrong</h3>
          <p className="mt-1 text-sm text-secondary-500">{error}</p>
          <button
            onClick={() => loadUsers(true)}
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            Try again
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-secondary-200">
          <div className="w-16 h-16 mx-auto rounded-full bg-secondary-100 flex items-center justify-center">
            <i className="ri-user-search-line text-2xl text-secondary-500" />
          </div>
          <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">
            {users.length === 0 ? 'No accounts yet' : 'No matching accounts'}
          </h3>
          <p className="mt-1 text-sm text-secondary-500">
            {users.length === 0
              ? 'New sign-ups will appear here automatically.'
              : 'Try adjusting your search.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((u) => {
            const isSuperAdmin = u.email === SUPER_ADMIN_EMAIL;
            const displayName = u.full_name || u.email || '—';

            return (
              <div
                key={u.user_id}
                className="bg-white rounded-lg border border-secondary-200 p-4 md:p-5"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-11 h-11 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center shrink-0">
                      <span className="font-heading text-base font-bold uppercase">
                        {displayName.charAt(0)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-secondary-900 break-all">{u.email}</p>
                        {isSuperAdmin && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-0.5 bg-secondary-900 text-white whitespace-nowrap">
                            <i className="ri-vip-crown-line" />
                            Super Admin
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-0.5 whitespace-nowrap ${
                            u.email_confirmed
                              ? 'bg-green-100 text-green-700'
                              : 'bg-accent-100 text-accent-900'
                          }`}
                        >
                          <i className={u.email_confirmed ? 'ri-mail-check-line' : 'ri-mail-close-line'} />
                          {u.email_confirmed ? 'Verified' : 'Unverified'}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-0.5 whitespace-nowrap bg-red-100 text-red-600">
                          <i className="ri-suitcase-line" />
                          {u.booking_count} {u.booking_count === 1 ? 'booking' : 'bookings'}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-secondary-500 truncate">
                        {u.full_name ? `${u.full_name} · ` : ''}
                        Joined {formatDate(u.created_at)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-0.5 text-xs text-secondary-500 md:text-right shrink-0">
                    <span className="inline-flex items-center gap-1.5 md:justify-end">
                      <i className="ri-login-circle-line text-secondary-400" />
                      {formatDateTime(u.last_sign_in_at)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}